import { parseColor } from './convert.js';

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(color: string): number {
  const rgb = parseColor(color);
  if (!rgb) return 0;
  return 0.2126 * channelLuminance(rgb.r) + 0.7152 * channelLuminance(rgb.g) + 0.0722 * channelLuminance(rgb.b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export type WcagLevel = 'AAA' | 'AA' | 'A' | 'fail';

export function wcagLevel(ratio: number): WcagLevel {
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'A';
  return 'fail';
}

export interface PickContrastingColorOptions {
  light?: string;
  dark?: string;
  /**
   * Contrast the light ink must reach to be kept. 3 is the WCAG bar for large
   * and bold UI text, which is what a filled button, badge or chip carries.
   */
  minLight?: number;
}

/**
 * Choose the ink for text sitting on `background`.
 *
 * This used to compare the background's relative luminance against a flat 0.5
 * and hand back white below it. Luminance is not contrast, and the proxy broke
 * for exactly the mid-luminance colours where the choice matters: amber
 * `#f59e0b` has a luminance of 0.464, so it took white ink at **2.15:1**, when
 * dark ink on the same amber is 9.6:1. Every filled Badge, Button, Chip and
 * Pagination control in the admin console, whose primary is that amber, was
 * unreadable.
 *
 * The light ink is still preferred, because white on a saturated brand colour
 * is the look these components are designed around: teal `#0d9488` keeps white
 * at 3.74:1. It is only given up when it cannot clear `minLight`, and then the
 * ink with the higher ratio wins outright.
 */
export function pickContrastingColor(background: string, opts: PickContrastingColorOptions = {}): string {
  const { light = '#ffffff', dark = '#0f172a', minLight = 3 } = opts;
  const lightRatio = contrastRatio(background, light);
  if (lightRatio >= minLight) return light;
  return contrastRatio(background, dark) > lightRatio ? dark : light;
}

export function isLight(color: string, threshold = 0.5): boolean {
  return relativeLuminance(color) > threshold;
}

export function isDark(color: string, threshold = 0.5): boolean {
  return !isLight(color, threshold);
}
